/* TEN BUT — Atribuição de origem no nível do PEDIDO (UTM → cart attributes → order.note_attributes)
 *
 * OBJETIVO: registrar de qual campanha/canal veio CADA PEDIDO, sobrevivendo ao consentimento.
 * Diferente da atribuição nativa do Shopify (customerJourneySummary), que é derrubada quando o
 * cliente recusa analytics/marketing no banner (setTrackingConsent(false) → momentsCount = 0),
 * este módulo grava a origem como DADO TRANSACIONAL do carrinho/pedido, não como tracking.
 *
 * BASE LEGAL (aprovada pelo jurídico): dado funcional/transacional de um pedido concluído, não
 * perfilamento de anônimo. Por isso:
 *  - Usa sessionStorage (escopo do tab, funcional — functionality_storage é `granted` por padrão),
 *    NUNCA cookie de marketing persistente.
 *  - NÃO chama gtag, NÃO toca na Shopify Customer Privacy API, NÃO depende do consentimento.
 *  - Só grava origem em atributos do carrinho (/cart/update.js), que o Shopify copia pro pedido.
 *
 * O CARRINHO É A MEMÓRIA ENTRE DIAS (bug #1043, 15/10/2026). O sessionStorage morre com a aba; o
 * carrinho da Shopify não. Por isso, em toda página de uma sessão com origem, o script lê /cart.js
 * e regrava o que faltar — sem trava "uma vez por sessão" — e no submit do checkout injeta os
 * atributos no próprio form#cart-form. Se o carrinho JÁ tem first-touch (tb_ts), ele vence o da
 * sessão: o first-touch é do carrinho, não da aba. Limite conhecido: sessão nova, sem origem na
 * URL, num carrinho NOVO não tem de onde tirar a origem (sem cookie/localStorage, por decisão
 * jurídica) — foi o caso do #1043, cujo carrinho de 14/10 tinha os tb_* e o do pedido não.
 *
 * MODELO: first-touch (campanha que TROUXE — não sobrescreve) + last-touch (último canal antes da
 * compra — sobrescreve a cada visita com origem nova). Os dois carimbados no pedido.
 *
 * Padrão herdado de ten-but-consent.js: IIFE + guard + DOMContentLoaded.
 */
(function () {
  if (window.__tbAttributionInit) return;
  window.__tbAttributionInit = true;

  var SESSION_KEY = 'tb_attribution';

  /* Parâmetros de origem que capturamos da URL de landing. */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  /* gbraid/wbraid: clique do Google Ads em iOS chega SEM gclid (só um destes). */
  var CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'];

  function safeReferrer() {
    try {
      var url = new URL(document.referrer);
      if (url.hostname === window.location.hostname) return '';
      return (url.origin + url.pathname).slice(0, 255);
    } catch (e) { return ''; }
  }

  function ssGet(key) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function ssSet(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  /* Lê os parâmetros de origem da URL atual. Retorna null se não houver NENHUM sinal de campanha. */
  function readTouchFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return null;
    }

    var touch = {};
    var hasSignal = false;

    UTM_KEYS.concat(CLICK_KEYS).forEach(function (k) {
      /* Story 19.1 (AC-F1): ÚLTIMA ocorrência, não a primeira. A URL do Shopping chega com
         UTM DUPLICADA — o feed do canal Google embute `utm_medium=product_sync...sag_organic`
         e o Google Ads anexa depois `utm_source=google&utm_medium=cpc&utm_campaign=...`.
         `params.get()` devolve a PRIMEIRA, e foi assim que os pedidos #1007 e #1008 foram
         gravados como listagem gratuita sendo CLIQUE PAGO. O GA4 e a Shopify leem a última. */
      var all = params.getAll(k);
      var v = all.length ? all[all.length - 1] : null;
      if (v) {
        touch[k] = v.slice(0, 255); /* defensivo: evita atributo gigante */
        hasSignal = true;
      }
    });

    var referrer = safeReferrer();
    if (!hasSignal && !referrer) return null;

    /* Story 19.1 (AC-F2): `gclid` na URL é prova de clique pago — vale mais que a UTM, que
       pode vir do feed orgânico do Shopping. Atributo ADITIVO (não altera as UTMs gravadas).
       Regra análoga com `fbclid` NÃO entra aqui (não pedida). */
    if (touch.gclid || touch.gbraid || touch.wbraid) touch.paid_click = 'google_ads';

    touch.referrer = referrer;
    touch.landing = (window.location.pathname + window.location.search).slice(0, 255);
    touch.ts = new Date().toISOString();
    return touch;
  }

  /* Achata first + last num objeto de atributos com prefixo tb_ (legível no admin do pedido).
     Só inclui chaves com valor (atributo vazio no Shopify = remoção). */
  function buildAttributes(state) {
    var attrs = {};
    function add(prefix, touch) {
      if (!touch) return;
      Object.keys(touch).forEach(function (k) {
        if (touch[k]) attrs[prefix + k] = touch[k];
      });
    }
    add('tb_', state.first);       /* first-touch: tb_utm_source, tb_referrer, tb_landing, tb_ts... */
    add('tb_last_', state.last);   /* last-touch:  tb_last_utm_source, tb_last_ts... */
    return attrs;
  }

  /* O que o carrinho ainda precisa receber. First-touch (tb_*) só entra se o carrinho não tiver
     nenhum (tb_ts é sempre gravado junto); last-touch (tb_last_*) entra se diferir.
     `cartAttrs` null = carrinho desconhecido (leitura falhou) → manda tudo. */
  function missingAttributes(attrs, cartAttrs) {
    var cartHasFirst = !!(cartAttrs && cartAttrs.tb_ts);
    var out = {};
    Object.keys(attrs).forEach(function (k) {
      var isFirst = k.indexOf('tb_last_') !== 0;
      if (isFirst && cartHasFirst) return;
      if (!cartAttrs || cartAttrs[k] !== attrs[k]) out[k] = attrs[k];
    });
    return out;
  }

  var knownCartAttrs = null;

  /* Grava os atributos no carrinho. Funciona com carrinho vazio (cria o token). Independente de consent. */
  function writeCartAttributes(attrs) {
    if (!attrs || !Object.keys(attrs).length) return Promise.resolve();
    try {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      })
        .then(function (res) {
          if (res && res.ok) {
            knownCartAttrs = knownCartAttrs || {};
            Object.keys(attrs).forEach(function (k) { knownCartAttrs[k] = attrs[k]; });
          }
        })
        .catch(function () {}); /* silencioso: atribuição nunca deve quebrar a navegação */
    } catch (e) {
      return Promise.resolve();
    }
  }

  /* Lê o carrinho e regrava só o que falta. Roda em TODA página da sessão com origem. */
  function syncCart() {
    var state = ssGet(SESSION_KEY);
    if (!state || (!state.first && !state.last)) return Promise.resolve();
    var attrs = buildAttributes(state);
    try {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res && res.ok ? res.json() : null; })
        .then(function (cart) { return (cart && cart.attributes) || null; })
        .catch(function () { return null; })
        .then(function (cartAttrs) {
          knownCartAttrs = cartAttrs;
          return writeCartAttributes(missingAttributes(attrs, cartAttrs));
        });
    } catch (e) {
      return Promise.resolve();
    }
  }

  /* Checkout: injeta os atributos como `attributes[...]` no form#cart-form. O POST nativo do
     /cart grava-os no carrinho e só então redireciona — sem segurar a navegação nem disputar
     corrida com um fetch. Inputs adicionados durante o evento submit entram no envio. */
  function injectIntoCheckoutForm(form) {
    var state = ssGet(SESSION_KEY);
    if (!state || (!state.first && !state.last)) return;
    /* last-touch vai SEMPRE (o carrinho pode ter sido trocado depois do último sync); o
       first-touch só se não se sabe de um tb_ts no carrinho. */
    var attrs = missingAttributes(buildAttributes(state),
      knownCartAttrs ? { tb_ts: knownCartAttrs.tb_ts } : null);
    Object.keys(attrs).forEach(function (k) {
      var name = 'attributes[' + k + ']';
      var input = null;
      for (var i = 0; i < form.elements.length; i++) {
        if (form.elements[i].name === name) { input = form.elements[i]; break; }
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-tb-attribution', '');
        form.appendChild(input);
      }
      input.value = attrs[k];
    });
  }

  /* Story 19.1 (AC-F3) — origem exposta para a instrumentação (REUSE > CREATE: a leitura de
     UTM não pode existir em dois lugares). LEITURA PURA do sessionStorage, sem efeito colateral
     e SEM depender do init: `ten-but.js` (theme.liquid L71) roda antes deste asset (L99) e este
     só grava no DOMContentLoaded — quem chama tem de poder chamar a qualquer momento.
     Devolve `null` quando não há origem; nunca lança. */
  window.tbAttribution = window.tbAttribution || {
    getLastTouch: function () {
      try {
        var state = ssGet(SESSION_KEY);
        return (state && state.last) || null;
      } catch (e) {
        return null;
      }
    }
  };

  function init() {
    var state = ssGet(SESSION_KEY) || { first: null, last: null };
    var touch = readTouchFromUrl();

    if (touch) {
      /* first-touch: só grava se ainda não houver (preserva a campanha que trouxe). */
      if (!state.first) state.first = touch;
      /* last-touch: sempre atualiza com a origem mais recente. */
      state.last = touch;
      ssSet(SESSION_KEY, state);
    }

    /* Toda página: confere o carrinho e regrava o que faltar (sem trava por sessão). Na 1ª página
       com origem isto já cria o carrinho com os tb_*, antes de qualquer add-to-cart. Sessão sem
       origem → syncCart não faz request. */
    syncCart();

    /* Rede de segurança: re-assere no add-to-cart (caso o carrinho tenha sido recriado). */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.getAttribute) return;
      if (/\/cart\/add/.test(form.getAttribute('action') || '')) {
        syncCart();
      } else if (form.id === 'cart-form') {
        injectIntoCheckoutForm(form);
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
